import type { MechanicDefinition } from "./types";

export const dialogueSystem: MechanicDefinition = {
  id: "dialogue_system",
  disabled: true,
  name: "Dialogue System",
  description: "Structured NPC conversations with persuasion, deception, and memory. Affects character agents directly.",
  icon: "MessageCircle",
  category: "narrative",
  aiHints: {
    purpose: "Ensures NPC dialogue uses character agents with memory, personality, and social dynamics.",
    whenToUse: "Worlds with rich NPC interactions, social gameplay, or conversation-heavy narratives.",
    synergies: ["reputation", "morality", "influence", "inner_voice"],
    conflicts: [],
    prerequisites: ["NPC entities with personality data"],
    tags: ["dialogue", "npc", "conversation", "social", "persuasion"],
    complexity: 2,
    exampleConfig: { depth: "detailed", memory: true },
    stateVariables: [],
  },
  configSchema: [
    {
      key: "style",
      label: "Dialogue Style",
      type: "select",
      options: [
        { value: "open", label: "Open (freeform conversation)" },
        { value: "choices", label: "Choices (offer 2-4 response options)" },
        { value: "hybrid", label: "Hybrid (choices for key moments)" },
      ],
      default: "hybrid",
    },
    {
      key: "npc_memory",
      label: "NPC Memory",
      type: "boolean",
      default: true,
      description: "NPCs remember past conversations via entity_memories and react accordingly",
    },
  ],
  systemPromptSection: (config) => {
    const style = config.style || "hybrid";
    const memory = config.npc_memory !== false;
    return `## DIALOGUE SYSTEM (Flow Mechanic — Narrator + Character Agents)
- Style: ${style}
- NPC Memory: ${memory ? "enabled" : "disabled"}
- ${style === "choices" ? "Always present 2-4 numbered dialogue options during NPC conversations. Include at least one persuasion and one aggressive option when contextually appropriate. Character agents MUST format their response with numbered options." : style === "hybrid" ? "Present numbered dialogue options at key decision points (first meetings, negotiations, confrontations). Allow freeform at other times. Character agents present options during key moments." : "Allow fully freeform conversation. NPCs respond naturally to whatever the player says."}
- ${memory ? "NPCs remember what the player has said and done. When invoking character agents, their entity_memories are injected into the prompt so they recall past interactions. NPCs who were lied to become suspicious. NPCs who were helped are more cooperative." : "NPCs don't track past conversations."}
- **NPC-TO-NPC RELATIONSHIPS**: NPCs have relationships with EACH OTHER, not just the player. Check _npc_relationships in state for inter-NPC dynamics (rival, ally, romantic, neutral with intensity 0-100). When multiple NPCs are present, their dialogue and behavior should reflect how they feel about each OTHER — rivals snipe, allies support, romantic interests show tension.
- **NPC INTENT AWARENESS**: Before narration, active NPCs are polled for their intent. NPC_INTENTS in the prompt show what each NPC wants to do in this moment — incorporate these naturally so NPCs feel like they have independent agency.
- For important conversations, the narrator should use invoke_character_agent to produce authentic NPC dialogue with full personality depth.
- Persuasion and deception attempts should trigger skill checks if that mechanic is active.
- Important dialogue choices should have meaningful consequences that feed into the reactive_world if active.`;
  },
  tools: [],
};
